import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';

const MIN_LENGTH = 16;

function checkRequirements(password: string) {
  return {
    length: password.length >= MIN_LENGTH,
    number: /\d/.test(password),
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
  };
}

export type StrongPasswordIndicatorProps = {
  password: string;
  /** Hide the indicator when password is empty. Default true. */
  hideWhenEmpty?: boolean;
  /** Typography variant for each requirement. Default 'body2'. */
  variant?: 'body2' | 'caption';
  sx?: React.ComponentProps<typeof Box>['sx'];
};

/**
 * Shows password strength requirements (16+ chars, number, upper, lower) with checkmarks.
 * Use below a password input; pass the current password value.
 */
export function StrongPasswordIndicator({
  password,
  hideWhenEmpty = true,
  variant = 'body2',
  sx,
}: StrongPasswordIndicatorProps) {
  if (hideWhenEmpty && !password) {
    return null;
  }

  const req = checkRequirements(password);
  const items: { met: boolean; label: string }[] = [
    { met: req.length, label: `At least ${MIN_LENGTH} characters` },
    { met: req.number, label: 'One number' },
    { met: req.uppercase, label: 'One uppercase letter' },
    { met: req.lowercase, label: 'One lowercase letter' },
  ];

  return (
    <Box sx={{ mt: 0.75, ...sx }} role="list" aria-label="Password requirements">
      {items.map(({ met, label }) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: met ? 'success.main' : 'text.secondary',
          }}
          role="listitem"
        >
          {met ? (
            <CheckCircleOutline sx={{ fontSize: 16 }} aria-hidden />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 16 }} aria-hidden />
          )}
          <Typography variant={variant} component="span">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
